/**
 * My prizes — every prize this guest has won, collected or not.
 *
 * Outstanding prizes carry their QR and typeable code here, so a guest can
 * collect one any time — days later, on another visit, after the win modal is
 * long gone. Collected prizes stay as a record of what they won and when.
 *
 * Lives in the profile as the "My prizes" accordion section. The data comes
 * in as a prop: EditUser4 owns the one subscription, because the section's
 * TITLE shows the count too, and two listeners could disagree.
 *
 * It used to render nothing at all for a guest with no prizes. That made the
 * feature invisible — and made "not deployed" look exactly like "deployed,
 * nothing won", which is how it went unfound on its first day. It now always
 * says what it is, even when empty.
 *
 * Data: users/{uid}/prizes (see services/prizeArchiveService.js).
 */
import React, { useEffect, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { prizeQrPayload, prizeCode, prizeDate } from "../services/prizeArchiveService";

const GOLD = "#F4B942";     // the brand's rare gold: a prize waiting for you
const MUTED = "#9AA0C0";

const card = {
  background: "rgba(255,255,255,0.05)",
  border: "1px solid rgba(255,255,255,0.1)",
  borderRadius: 16,
  padding: 16,
};

const heading = {
  fontSize: "0.85rem",
  fontWeight: 700,
  letterSpacing: "0.14em",
  textTransform: "uppercase",
  color: MUTED,
  margin: "0 0 10px",
};

function CopyCode({ code }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (_) {
      // Clipboard refused (some in-app browsers): select the text instead so a
      // long-press can copy it.
      const el = document.getElementById(`prize-code-${code}`);
      if (el && window.getSelection) {
        const r = document.createRange();
        r.selectNodeContents(el);
        const sel = window.getSelection();
        sel.removeAllRanges();
        sel.addRange(r);
      }
    }
  };
  return (
    <div style={{ marginTop: 12 }}>
      <div style={{ fontSize: "1rem", color: MUTED, marginBottom: 4 }}>
        Or give staff this code:
      </div>
      <div
        id={`prize-code-${code}`}
        style={{ fontFamily: "ui-monospace, Menlo, monospace", fontSize: "1rem", color: "#fff", wordBreak: "break-all", lineHeight: 1.4 }}
      >
        {code}
      </div>
      <button
        type="button"
        onClick={copy}
        style={{ marginTop: 8, padding: "8px 16px", minHeight: 44, borderRadius: 999, border: "1px solid rgba(255,255,255,0.3)", background: "transparent", color: "#fff", fontSize: "1rem", fontWeight: 600, cursor: "pointer" }}
      >
        {copied ? "Copied" : "Copy code"}
      </button>
    </div>
  );
}

function OutstandingPrize({ p, uid, open, onToggle }) {
  // Prefer the payload saved at win time; rebuild from the fields for any
  // record that was backfilled without one. Same helper either way.
  const ids = { venueUid: p.venueUid, djchatId: p.djchatId || p.id, uid, prizeId: p.prizeId };
  const qr = p.qr || prizeQrPayload(ids);
  const code = p.code || prizeCode(ids);

  return (
    <div style={{ ...card, borderColor: "rgba(244,185,66,0.45)" }}>
      <div style={{ fontSize: "1.25rem", fontWeight: 700, color: "#fff", lineHeight: 1.25 }}>
        <span role="img" aria-label="prize">🎁</span> {p.prizeDescription || "Your prize"}
      </div>
      <div style={{ fontSize: "1rem", color: MUTED, marginTop: 6, lineHeight: 1.45 }}>
        {p.venueName ? `at ${p.venueName}` : "Prize"}{p.wonAt ? ` · won ${prizeDate(p.wonAt)}` : ""}
      </div>
      {(p.trackTitle || p.trackArtist) && (
        <div style={{ fontSize: "1rem", color: MUTED, marginTop: 2, lineHeight: 1.45 }}>
          for “{p.trackTitle}”{p.trackArtist ? ` — ${p.trackArtist}` : ""}
        </div>
      )}

      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        style={{ marginTop: 14, width: "100%", minHeight: 48, borderRadius: 999, border: "none", background: GOLD, color: "#1a1206", fontSize: "1rem", fontWeight: 700, cursor: "pointer" }}
      >
        {open ? "Hide code" : "Show QR to collect"}
      </button>

      {open && (
        <div style={{ marginTop: 14, textAlign: "center" }}>
          <div style={{ display: "inline-block", background: "#fff", padding: 12, borderRadius: 12 }}>
            <QRCodeSVG value={JSON.stringify(qr)} size={200} level="M" />
          </div>
          <div style={{ fontSize: "1rem", color: "#fff", marginTop: 10 }}>
            Show this to venue staff to collect your prize.
          </div>
          <div style={{ textAlign: "left" }}>
            <CopyCode code={code} />
          </div>
        </div>
      )}
    </div>
  );
}

function CollectedPrize({ p }) {
  return (
    <div style={{ display: "flex", gap: 12, alignItems: "flex-start", padding: "12px 0", borderTop: "1px solid rgba(255,255,255,0.08)" }}>
      <span style={{ color: "#86efac", fontSize: "1.1rem", lineHeight: 1.4 }} aria-hidden="true">✓</span>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: "1rem", fontWeight: 600, color: "#fff", lineHeight: 1.4 }}>
          {p.prizeDescription || "Prize"}
        </div>
        <div style={{ fontSize: "1rem", color: MUTED, lineHeight: 1.45 }}>
          {p.venueName ? `at ${p.venueName} · ` : ""}collected {prizeDate(p.redeemedAt) || "—"}
          {p.redeemedByStaffName ? ` · by ${p.redeemedByStaffName}` : ""}
        </div>
      </div>
    </div>
  );
}

export default function PrizeArchive({ uid, archive }) {
  const [openId, setOpenId] = useState(null);
  const outstanding = archive?.outstanding || [];
  const redeemed = archive?.redeemed || [];

  // With exactly one prize waiting, open it: that is why they came here.
  useEffect(() => {
    if (outstanding.length === 1 && openId === null) setOpenId(outstanding[0].id);
  }, [outstanding, openId]);

  if (!outstanding.length && !redeemed.length) {
    return (
      <div style={{ padding: "4px 2px 8px" }}>
        <p style={{ fontSize: "1rem", color: "#fff", margin: "0 0 6px", lineHeight: 1.5 }}>
          No prizes yet.
        </p>
        <p style={{ fontSize: "1rem", color: MUTED, margin: 0, lineHeight: 1.5 }}>
          When you win a prize draw it is saved here, with the QR code to collect
          it — so you can claim it whenever you are next at the venue.
        </p>
      </div>
    );
  }

  return (
    <section style={{ padding: "4px 0 8px" }} aria-label="My prizes">

      {outstanding.length > 0 && (
        <>
          <p style={heading}>Ready to collect · {outstanding.length}</p>
          <div style={{ display: "grid", gap: 12, marginBottom: redeemed.length ? 20 : 0 }}>
            {outstanding.map((p) => (
              <OutstandingPrize
                key={p.id}
                p={p}
                uid={uid}
                open={openId === p.id}
                onToggle={() => setOpenId(openId === p.id ? "" : p.id)}
              />
            ))}
          </div>
        </>
      )}

      {redeemed.length > 0 && (
        <div style={card}>
          <p style={{ ...heading, margin: "0 0 2px" }}>Collected · {redeemed.length}</p>
          {redeemed.map((p) => <CollectedPrize key={p.id} p={p} />)}
        </div>
      )}
    </section>
  );
}
